import * as React from "react";
import { Box, Typography, CircularProgress, Alert, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { fetchTherapistPatientOrders, fetchTherapistReportLink } from "../../Services/apiService";
import OrderCard from "../../components/portal/OrderCard";
import StatusFilterChips from "../../components/portal/StatusFilterChips";
import PortalPagination from "../../components/portal/PortalPagination";
import { C } from "../../theme/dashboardCardTokens";
import { useTranslation } from "../../i18n/LanguageContext";

// The "go to tests" click-through from My Patients — same OrderCard used on
// the patient side, role="therapist" so no "Fill out your form" chip.
// Supports the same progressStatus filter + pagination as PatientOrders.
function TherapistPatientOrders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { patientKey } = useParams();
  const [orders, setOrders] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [progressStatus, setProgressStatus] = React.useState("all");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const handleFilterChange = (status) => {
    setProgressStatus(status);
    setPage(1);
  };

  // A different patientKey means a fresh page — reset filter/page state.
  React.useEffect(() => {
    setPage(1);
    setProgressStatus("all");
  }, [patientKey]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchTherapistPatientOrders(patientKey, page, 20, progressStatus)
      .then((res) => {
        if (cancelled) return;
        setOrders(res.data?.orders || []);
        setPagination(res.data?.pagination || null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) {
          navigate("/dashboard", { replace: true });
          return;
        }
        console.error("fetchTherapistPatientOrders failed:", { status: err.status, message: err.message, err });
        setError(
          err.status === 400
            ? t("portal.orders.therapist.invalidPatient")
            : err.message || t("portal.orders.therapist.loadError")
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientKey, page, progressStatus, navigate]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => navigate("/therapist/patients")}
          aria-label={t("portal.orders.therapist.backLabel")}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t("portal.orders.therapist.title")}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 5 }}>
        {t("portal.orders.therapist.subtitle")}
      </Typography>

      <StatusFilterChips value={progressStatus} onChange={handleFilterChange} />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && orders.length === 0 && (
        <Box sx={{ p: 4, textAlign: "center", color: C.textSecondary }}>
          {progressStatus === "all"
            ? t("portal.orders.therapist.emptyAll")
            : t("portal.orders.therapist.emptyFiltered")}
        </Box>
      )}

      {!loading &&
        !error &&
        orders.map((order, index) => (
          <OrderCard
            key={order.order_id}
            order={order}
            role="therapist"
            getReportLink={fetchTherapistReportLink}
            defaultExpanded={index === 0}
          />
        ))}

      {!loading && !error && <PortalPagination pagination={pagination} onChange={setPage} />}
    </Box>
  );
}

export default TherapistPatientOrders;
